import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  lazy,
  Suspense
} from 'react';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
import ShoppingCartContext from '../../../contexts/ShoppingCartContext';
import httpHandler from '../../../helpers/httpHandler';
import DomainInfo from '../../../helpers/DomainInfo';
import { toAnchorId, ANCHOR_SCROLL_MARGIN_PX } from './pageAnchors';
import { permissionFor } from '../elements/rulePermission';

const Text = lazy(() => import('../elements/Text'));
const CustomButton = lazy(() => import('../elements/Button'));
const Image = lazy(() => import('../elements/Image'));
const PDF = lazy(() => import('../elements/PDF'));
const DividerElement = lazy(() => import('../elements/divider/Divider'));
const Empty = lazy(() => import('../elements/empty/Empty'));
const Conferences = lazy(() => import('../elements/conferences/Conferences'));
const Courses = lazy(() => import('../elements/Courses'));
const Card = lazy(() => import('../elements/Card'));
const TabsPages = lazy(() => import('../elements/tabsPages/TabsPages'));
const NavBar = lazy(() => import('../elements/navBar/NavBar'));
const Carousel = lazy(() => import('../elements/carousel/Carousel'));
const AddToCartModal = lazy(() => import('../../commerce/AddToCartModal'));
const CourseCatalog = lazy(() => import('../elements/CourseCatalog'));
const TeamChart = lazy(() => import('../elements/teamChart/TeamChart'));
const Video = lazy(() => import('../elements/Video'));
//ViewRecording is a plain-js class component exported unwrapped, so ts infers
//no props for it (unlike Card/Conferences, which withRouter() widens to any)
const ViewRecording = lazy(
  () =>
    import('../elements/conferences/viewRecording/ViewRecording') as Promise<{
      default: React.ComponentType<any>;
    }>
);

//View-only PDF reader (no download control), clickAction 'view_document'. Added 11/03/2026 for the BSM 2026
//workbook at the top of /bsm2026; same server-side gate as ViewRecording (recordingsForOccurrence).
const ViewDocument = lazy(
  () => import('../elements/conferences/viewDocument/ViewDocument')
);

type Props = {
  element: any;
  handlePopup: (popupId: number) => void;
  ruleAccess: any;
  /** false while the server is still evaluating this user's rule sets */
  ruleAccessReady?: boolean;
};

const ElementViewer = ({
  element,
  handlePopup,
  ruleAccess,
  ruleAccessReady
}: Props) => {
  const shoppingCartContext = useContext(ShoppingCartContext);
  const [addingProductToCart, setAddingProductToCart] =
    useState<boolean>(false);
  //only the occurrence id is carried. the rule set gating the recording is resolved
  //server-side from the database by /recordingsForOccurrence, so it is deliberately
  //not stored in the page json or sent from the client
  const [viewingRecording, setViewingRecording] = useState<{
    id: number;
  } | null>(null);
  //the same occurrence-only contract as viewingRecording, for the view-only reader
  const [viewingDocument, setViewingDocument] = useState<{
    id?: number;
    src?: string;
    title?: string;
  } | null>(null);
  const navigate = useNavigate();
  //A click on a gated element that came in before its verdict. Held here and replayed
  //once the verdict lands, so the tile can be shown (and clicked) at once without the
  //click ever being decided on a guess.
  const heldClick = useRef<any>(null);
  const [clickPending, setClickPending] = useState<boolean>(false);

  const handleClick = async (item: any, hasPermission?: boolean) => {
    //`undefined` here means the rule-set verdict has not arrived — NOT that access was
    //denied. Acting on it sent entitled, paying members to the upsell page and could
    //fire applyNoPermissionTag against their Keap record. Must be an identity check:
    //`false` is a real verdict and has to keep working.
    if (hasPermission === undefined) {
      if (ruleAccessReady) {
        //The check is over and still has no answer (it failed twice, see
        //PageViewer.fetchRuleAccess). Say so rather than doing nothing.
        message.warning(
          'We could not check your access just now. Please refresh the page and try again.'
        );
        return;
      }
      heldClick.current = item;
      setClickPending(true);
      return;
    }
    heldClick.current = null;
    setClickPending(false);
    if (!hasPermission) {
      if (item.redirect_on_no_permission) {
        handleNoPermissionRedirect(item);
      }
    } else if (
      item.clickAction === 'googleLogin' ||
      item.clickAction === 'createGoogleAccount'
    ) {
      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${DomainInfo.googleClientId}&redirect_uri=${window.location.origin}&response_type=code&scope=email profile&state=google`;
    } else if (
      item.clickAction === 'microsoftLogin' ||
      item.clickAction === 'createMicrosoftAccount'
    ) {
      window.location.href = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${DomainInfo.microsoftClientId}&response_type=code&redirect_uri=${encodeURIComponent(
        window.location.origin
      )}&response_mode=query&scope=openid%20email%20offline_access%20profile%20https%3A%2F%2Fgraph.microsoft.com%2FUser.Read&state=microsoft`;
    } else if (
      item.clickAction === 'facebookLogin' ||
      item.clickAction === 'createAccount'
    ) {
      console.log(
        'handling click',
        `https://www.facebook.com/v12.0/dialog/oauth?client_id=${DomainInfo.facebookClientId}&state=facebook&scope=email&redirect_uri=${
          window.location.origin
        }/`
      );
      //old id 2412946138970427
      window.location.href = `https://www.facebook.com/v12.0/dialog/oauth?client_id=${DomainInfo.facebookClientId}&state=facebook&scope=email&redirect_uri=${
        window.location.origin
      }`;
    } else if (item.clickAction === 'redirect') {
      if (item.applyTag) {
        try {
          httpHandler.post('/api/users/applyTag', {
            tagId: item.applyTag.id
          });
        } catch (error) {
          console.error(error);
        }
      }
      handleRedirect(item);
    } else if (item.clickAction === 'addToShoppingCart') {
      addProduct();
      setAddingProductToCart(true);
    } else if (item.clickAction === 'display_pop_up') {
      handlePopup(item.popupId);
    } else if (item.clickAction === 'view_recording') {
      setViewingRecording({ id: item.occurrence_id });
    } else if (item.clickAction === 'view_document') {
      setViewingDocument({
        id: item.occurrence_id,
        src: item.documentUrl,
        title: item.text
      });
    }
  };
  //Replay a held click as soon as its own rule set is answered (or the whole check
  //has ended). handleClick re-reads the verdict, so a failed check ends in the
  //warning above, never in the not-entitled redirect.
  useEffect(() => {
    const item = heldClick.current;
    if (!item) return;
    const verdict = permissionFor(item, ruleAccess);
    if (verdict === undefined && !ruleAccessReady) return;
    heldClick.current = null;
    setClickPending(false);
    handleClick(item, verdict);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleAccess, ruleAccessReady]);

  const addProduct = () => {
    httpHandler
      .post(`/api/commerce/products/getProductByInfusionsoftId`, {
        id: element.details.product.product.infusionsoft_id
      })
      .then((product) => {
        shoppingCartContext.addToCart(product[0]);
      })
      .catch((error) => console.error('Error found: ', error));
  };
  const handleRedirect = (item: any) => {
    if (item.redirectType === 'local') {
      navigate(`/${item.url}`);
    } else if (item.redirectType === 'external') {
      //A held click is replayed a moment after the real click, and a browser may block
      //a new tab that late. Fall back to opening the link here rather than losing it.
      const tab = window.open('https://' + item.url, '_blank');
      if (!tab) window.location.href = 'https://' + item.url;
    }
  };
  const handleNoPermissionRedirect = (item: any) => {
    //apply no permission tag
    if (item.applyNoPermissionTag) {
      httpHandler.post('/api/users/applyTag', {
        tagId: item.applyNoPermissionTag.id
      });
    }

    if (element.details.no_permission_redirect_type === 'local') {
      navigate(`/${element.details.no_permission_url}`);
    } else if (element.details.no_permission_redirect_type === 'external') {
      window.location.href = 'https://' + element.details.no_permission_url;
    }
  };

  const getComponent = () => {
    switch (element.type) {
      case 'Text':
        return <Text element={element} />;
      case 'NavBar':
        return (
          <NavBar element={element} ruleAccess={ruleAccess} editMode={false} />
        );
      case 'Button':
        return (
          <CustomButton
            element={element}
            handleClick={handleClick}
            ruleAccess={ruleAccess}
            ruleAccessReady={ruleAccessReady}
            clickPending={clickPending}
          />
        );
      case 'Image':
        return (
          <Image
            element={element}
            pointer={{ cursor: 'pointer' }}
            building={false}
            handleClick={handleClick}
            ruleAccess={ruleAccess}
            ruleAccessReady={ruleAccessReady}
            clickPending={clickPending}
          />
        );
      case 'Video':
        return <Video element={element} />;
      case 'PDF':
        return <PDF element={element} />;
      case 'Divider':
        return <DividerElement element={element} />;
      case 'Card':
        return (
          <Card
            element={element}
            building={false}
            handleClick={handleClick}
            ruleAccess={ruleAccess}
            ruleAccessReady={ruleAccessReady}
            clickPending={clickPending}
          />
        );
      case 'Conferences':
        return (
          <Conferences
            element={element}
            ruleAccess={ruleAccess}
            ruleAccessReady={ruleAccessReady}
          />
        );
      case 'Courses':
        return <Courses element={element} />;
      case 'CourseCatalog':
        return <CourseCatalog element={element} />;
      case 'Tabs':
        return <TabsPages element={element} />;
      case 'Carousel':
        return (
          <Carousel
            element={element}
            handleClick={handleClick}
            ruleAccess={ruleAccess}
            ruleAccessReady={ruleAccessReady}
            building={false}
            active={true}
          />
        );
      case 'TeamChart':
        return <TeamChart element={element} />;
      case 'Empty':
        return <Empty element={element} />;
      default:
        return null;
    }
  };
  const handleCloseCartModal = () => {
    setAddingProductToCart(false);
  };

  //Optional "anchor" from the builder: /page#anchor scrolls here (pageAnchors.js).
  const anchorId = toAnchorId(element.details?.anchor);
  return (
    <div
      id={anchorId}
      //The element's type and its custom-CSS role (styleRole.js) on the wrapper, so a page's custom CSS can
      //reach e.g. "the wrapper of the logo image" as [data-ve-el="logo"] instead of div:has(img), which the
      //browser re-checks on every change inside the page (BSM 2026 agenda, 09/30/2026: slow on phones).
      data-pb-el={element.type}
      data-ve-el={element.styles?.['--ve']}
      style={{
        position: 'relative',
        display: 'inline-block',
        width: '100%',
        textAlign: element.details ? element.details.alignment : 'center',
        ...(anchorId ? { scrollMarginTop: `${ANCHOR_SCROLL_MARGIN_PX}px` } : {})
      }}
    >
      <Suspense fallback={<div>loading</div>}>
        {element.details == null ? '' : getComponent()}
      </Suspense>

      {addingProductToCart ? (
        <AddToCartModal
          addToCart={addingProductToCart}
          product={element.details.product.product}
          closeModal={handleCloseCartModal}
        />
      ) : (
        ''
      )}

      {viewingDocument ? (
        <Suspense fallback={null}>
          <ViewDocument
            occurrenceId={viewingDocument.id}
            src={viewingDocument.src}
            title={viewingDocument.title}
            onClose={() => setViewingDocument(null)}
          />
        </Suspense>
      ) : (
        ''
      )}

      {viewingRecording ? (
        <ViewRecording
          display={true}
          occurrence={viewingRecording}
          cancelViewRecording={() => setViewingRecording(null)}
        />
      ) : (
        ''
      )}
    </div>
  );
};
export default ElementViewer;
